import { assertNever } from '@gitbook/util-ts';

import { apiClient, type SiteStructureContent } from '../../../state';

/**
 * Update the draft state for a site structure item.
 */
export async function updateSiteContentDraftState(props: {
    organizationId: string;
    siteId: string;
    content: SiteStructureContent;
    draft: boolean;
}): Promise<void> {
    const { organizationId, siteId, content, draft } = props;

    switch (content.object) {
        case 'site-space':
            await apiClient.siteSpaces.updateSiteSpaceById(
                organizationId,
                siteId,
                content.id,
                { draft },
                { returnPreference: 'minimal' }
            );
            break;
        case 'site-section': {
            const singleSiteSpace = content.siteSpaces.length === 1 ? content.siteSpaces[0] : null;
            await apiClient.siteSections.updateSiteSectionById(
                organizationId,
                siteId,
                content.id,
                { draft },
                { returnPreference: 'minimal' }
            );
            // If the section is in a single space, update the draft state of that space as well.
            if (singleSiteSpace) {
                try {
                    await apiClient.siteSpaces.updateSiteSpaceById(
                        organizationId,
                        siteId,
                        singleSiteSpace.id,
                        { draft },
                        { returnPreference: 'minimal' }
                    );
                } catch (error) {
                    try {
                        // If the site-space update fails, try to revert the site-section update to keep the state consistent.
                        await apiClient.siteSections.updateSiteSectionById(
                            organizationId,
                            siteId,
                            content.id,
                            { draft: content.draft },
                            { returnPreference: 'minimal' }
                        );
                    } catch {
                        // Keep the original site-space update error visible to the caller.
                    }

                    // Rethrow the original site-space update error to be handled by the caller who shows an error toast.
                    throw error;
                }
            }
            break;
        }
        case 'site-section-group':
            await apiClient.siteSectionGroups.updateSiteSectionGroupById(
                organizationId,
                siteId,
                content.id,
                { draft },
                { returnPreference: 'minimal' }
            );
            break;
        case 'site-external-link':
            await apiClient.siteExternalLinks.updateSiteExternalLinkById(
                organizationId,
                siteId,
                content.id,
                { draft },
                { returnPreference: 'minimal' }
            );
            break;
        default:
            assertNever(content);
    }
}
